// GAVION off-CRM call (owner OK 2026-10-26): « Inscrire un appel fait hors CRM ». A call made from a cell and its
// result, logged at once (SQL public.log_off_crm_call, 86_): same result form and rules as the dialer
// (CallOutcomeDialog), plus when it happened and who was called (the lead's main line or one of its contacts).
import { useGetList, useTranslate } from "ra-core";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { celebratesOutcome, launchConfetti } from "../../celebrate/confetti";
import { useGavionConfig } from "../../config/useGavionConfig";
import {
  DIAL_OUTCOMES,
  validateCallbackDay,
} from "../../domain/dialer/outcomes";
import type { DialOutcome } from "../../domain/dialer/types";
import { useDialerApi } from "../api/DialerApiContext";
import { DialerApiError } from "../api/types";
import type { FieldError } from "../screen/dialerReducer";
import { OutcomeForm } from "../screen/OutcomeForm";
import {
  consentEmail,
  emptyOutcomeForm,
  formToPayloadExtras,
} from "../screen/outcomeFormModel";
import { useRecordConsent } from "../screen/useRecordConsent";

type Props = {
  companyId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone: () => void;
  leadTimezone?: string | null;
  leadEmail?: string | null;
  now?: () => Date;
};

type ContactRow = {
  id: number;
  first_name?: string | null;
  last_name?: string | null;
};

const MAIN_LINE = "main";
const pad = (n: number) => String(n).padStart(2, "0");
/** A datetime-local value in the browser's zone (the rep's own clock). */
export const toLocalInput = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;

export const OffCrmCallDialog = ({
  companyId,
  open,
  onOpenChange,
  onDone,
  leadTimezone: tzProp,
  leadEmail = null,
  now = () => new Date(),
}: Props) => {
  const t = useTranslate();
  const leadTimezone = tzProp ?? "America/Toronto";
  const api = useDialerApi();
  const recordConsent = useRecordConsent();
  const { config } = useGavionConfig();
  const { data: contacts = [] } = useGetList<ContactRow>(
    "contacts",
    {
      filter: { company_id: companyId },
      pagination: { page: 1, perPage: 50 },
      sort: { field: "last_name", order: "ASC" },
    },
    { enabled: open },
  );
  const [calledAt, setCalledAt] = useState(() => toLocalInput(now()));
  const [who, setWho] = useState(MAIN_LINE);
  const [outcome, setOutcome] = useState<DialOutcome | null>(null);
  const [form, setForm] = useState(emptyOutcomeForm);
  const [error, setError] = useState<FieldError | null>(null);
  const [saving, setSaving] = useState(false);
  const known = (code: string) =>
    t(`gavion.dialer.errors.${code}`, { _: "__missing__" }) !== "__missing__"
      ? code
      : "unknown";

  const save = async () => {
    if (!outcome || saving) return;
    setError(null);
    const at = new Date(calledAt);
    if (Number.isNaN(at.getTime())) {
      setError({ code: "called_at_required" });
      return;
    }
    const call = {
      outcome,
      ...formToPayloadExtras(form, outcome, leadTimezone),
    };
    // GAVION missed-call: no callback on a weekend / Dec 25 / Jan 1 (suggestion: next business day, same time)
    const day = validateCallbackDay(
      { ...call, call_id: 0, version: 1 }, // the call does not exist yet: only its callback is read
      leadTimezone,
    );
    if (!day.ok) {
      setError({ code: day.code, suggestion: day.suggestion });
      return;
    }
    setSaving(true);
    let callId: number;
    try {
      const saved = await api.logOffCrmCall({
        company_id: companyId,
        contact_id: who === MAIN_LINE ? null : Number(who),
        called_at: at.toISOString(),
        call,
      });
      callId = saved.id;
    } catch (e) {
      const code = e instanceof DialerApiError ? e.code : "unknown";
      setError({
        // « forbidden » here: this user cannot dial (the dialer's own text says « admins only »)
        code: code === "forbidden" ? "off_crm_forbidden" : known(code),
        suggestion: e instanceof DialerApiError ? e.detail : undefined,
      });
      setSaving(false);
      return;
    }
    if (celebratesOutcome(outcome)) launchConfetti(); // GAVION celebrations
    const email = consentEmail(form, outcome, leadEmail);
    if (email)
      await recordConsent({
        email,
        companyId,
        callId,
        obtainedAt: now().toISOString(),
        offline: false,
      });
    setSaving(false);
    onDone();
  };

  const contactName = (c: ContactRow) =>
    [c.first_name, c.last_name].filter((x) => x && x.trim()).join(" ") ||
    t("gavion.dialer.contact_call.unnamed");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t("gavion.dialer.off_crm.title")}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground">
          {t("gavion.dialer.off_crm.intro")}
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1">
            <Label htmlFor="oc-when">{t("gavion.dialer.off_crm.when")}</Label>
            <Input
              id="oc-when"
              type="datetime-local"
              value={calledAt}
              max={toLocalInput(now())}
              onChange={(e) => {
                setCalledAt(e.target.value);
                setError(null);
              }}
            />
          </div>
          <div className="grid gap-1">
            <Label htmlFor="oc-who">{t("gavion.dialer.off_crm.who")}</Label>
            <select
              id="oc-who"
              className="h-9 w-full rounded-md border bg-background px-2 text-sm"
              value={who}
              onChange={(e) => setWho(e.target.value)}
            >
              <option value={MAIN_LINE}>
                {t("gavion.dialer.off_crm.main_line")}
              </option>
              {contacts.map((c) => (
                <option key={c.id} value={String(c.id)}>
                  {contactName(c)}
                </option>
              ))}
            </select>
          </div>
        </div>
        <Label id="oc-outcome">{t("gavion.dialer.todo.outcome")}</Label>
        <RadioGroup
          aria-labelledby="oc-outcome"
          value={outcome ?? ""}
          onValueChange={(v) => {
            setOutcome(v as DialOutcome);
            setError(null);
          }}
          className="grid grid-cols-1 gap-2 sm:grid-cols-2"
        >
          {DIAL_OUTCOMES.map((o) => (
            <div key={o.code} className="flex items-center gap-2">
              <RadioGroupItem id={`oc-${o.code}`} value={o.code} />
              <Label htmlFor={`oc-${o.code}`}>
                {t(`gavion.dialer.outcomes.${o.code}`)}
              </Label>
            </div>
          ))}
        </RadioGroup>
        {outcome && (
          <OutcomeForm
            outcome={outcome}
            form={form}
            setForm={setForm}
            error={error}
            leadEmail={leadEmail}
            leadTimezone={leadTimezone}
            now={now}
            callbackTime={config.callback_default_time}
          />
        )}
        <DialogFooter>
          <Button disabled={!outcome || saving} onClick={() => void save()}>
            {t("gavion.dialer.form.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
